/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import React, { memo, useCallback, useEffect, useState } from 'react';
import ListItem from '../list-item';
import './styles.scss';
import { ListContainerProps } from './types';
import { generateID, generateUniqueIdentifier } from '../../base/helper';

/**
 * UI Toolkit List Container component
 * @public
 */
const ListContainer: React.FC<ListContainerProps> = ({
  items,
  'aria-label': ariaLabel,
  selectedIndex,
  id = generateID('clientos-ui-toolkit-list-container'),
  ...props
}) => {
  const [selectedItem, setSelectedItem] = useState<number>(-1);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const onClick = useCallback(
    (index: number) => {
      if (items[index].disabled) return;
      if (!items[index].onClick) return;

      if (selectedItem === index) {
        setSelectedItem(-1);
        setSelectedItemId(null);
      } else {
        setSelectedItem(index);
        setSelectedItemId(generateUniqueIdentifier(items[index]));
      }

      items[index].onClick();
    },
    [items, selectedItem]
  );

  const onMouseEnter = useCallback(
    (index: number) => {
      if (items[index]?.disabled) return;
      if (!items[index].onMouseEnter) return;

      items[index].onMouseEnter();
    },
    [items]
  );

  const onMouseLeave = useCallback(
    (index: number) => {
      if (items[index]?.disabled) return;
      if (!items[index].onMouseLeave) return;

      items[index].onMouseLeave();
    },
    [items]
  );

  const onHover = useCallback(
    (index: number) => {
      if (items[index]?.disabled) return;
      if (!items[index].onHover) return;

      items[index].onHover();
    },
    [items]
  );

  useEffect(() => {
    if (
      selectedItem &&
      items[selectedItem] &&
      selectedItemId &&
      generateUniqueIdentifier(items[selectedItem]) !== selectedItemId
    ) {
      setSelectedItem(-1);
      setSelectedItemId(null);
    }
  }, [items, selectedItem, selectedItemId]);

  return (
    <div
      className="clientos-ui-toolkit-list-container"
      role="list"
      aria-label={ariaLabel}
      id={id}
    >
      {items.map((item, index) => (
        <ListItem
          id={item.id}
          key={index}
          item={item}
          index={index}
          selectedIndex={selectedIndex ?? selectedItem}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onHover={onHover}
          dividerColor={props?.dividerColor}
        />
      ))}
    </div>
  );
};

export default memo(ListContainer);
